import React from 'react';
import { Activity, MessageSquare, Award, BookOpen } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AnalyticsDashboardTab = ({ dashboardData }) => {
  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 dark:text-gray-400 mt-4">Loading dashboard...</p>
      </div>
    );
  }

  // Chart configurations
  const engagementTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Engagement Score',
        data: [65, 70, 68, 75, 72, 78],
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const recognitionCategoryData = {
    labels: ['Team Player', 'Innovation', 'Leadership', 'Customer Focus'],
    datasets: [
      {
        data: [30, 25, 20, 25],
        backgroundColor: ['#EF4444', '#10B981', '#F59E0B', '#8B5CF6'],
      },
    ],
  };

  const trainingCompletionData = {
    labels: ['Safety', 'Technical', 'Leadership', 'Compliance'],
    datasets: [
      {
        label: 'Completion Rate %',
        data: [85, 72, 68, 91],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
      },
    ],
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Engagement Score
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData.overall_engagement_score}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <MessageSquare className="w-8 h-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Survey Responses
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData.feedback_analytics.total_responses}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <Award className="w-8 h-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Recognitions
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData.recognition_analytics.recognitions_this_month}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <BookOpen className="w-8 h-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Training Completion
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData.training_analytics.average_completion_rate.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Engagement Trend
          </h3>
          <Line data={engagementTrendData} />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recognition Categories
          </h3>
          <Doughnut data={recognitionCategoryData} />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Training Completion by Category
        </h3>
        <Bar data={trainingCompletionData} />
      </div>
    </div>
  );
};

export default AnalyticsDashboardTab;
